import React, { ComponentType, useMemo } from 'react';

import {
  REPORT_TYPES,
  importRemote,
  report,
  getRemoteRuntime,
  handleRemoteImportError,
} from '@adsk/exo-skeleton-api';
import {
  safeStringify,
  wrapWithEventPropertiesPrefix,
} from '@adsk/exo-skeleton-api/internal';

import {
  EXO_SKELETON_SCOPE_NAME,
  RETRY_IMPORT_REMOTE,
} from '../helpers/consts';
import { useRemotes } from '../providers/RemotesProvider';

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export function useCreateLazyRemoteComponent<PROPS extends {} = {}>({
  moduleName,
  scope,
  version,
  timeoutMs,
}: {
  scope: string;
  moduleName: string;
  version?: string;
  timeoutMs?: number | false;
}) {
  const { moniker, override } = getRemoteRuntime(scope) || {};
  const { isLoading } = useRemotes() || { isLoading: false };

  return useMemo(
    () =>
      React.lazy(() =>
        isLoading
          ? new Promise(() => void 0)
          : importRemote<{ default: ComponentType<PROPS> }>({
              scope,
              module: moduleName,
              version,
              timeoutMs,
              onRetry({ error, retryName, tryAttempt }) {
                const context = {
                  moniker,
                  override,
                  retryName,
                  tryAttempt,
                  module,
                  scope,
                  error,
                };

                report({
                  type: REPORT_TYPES.error,
                  scope: EXO_SKELETON_SCOPE_NAME,
                  value: RETRY_IMPORT_REMOTE,
                  context: {
                    ...context,
                    ...wrapWithEventPropertiesPrefix({
                      data: safeStringify({
                        ...context,
                        errorMessage: (error as Error)?.message,
                      }),
                      message: RETRY_IMPORT_REMOTE,
                      stack: (error as Error)?.stack,
                      type: REPORT_TYPES.error,
                    }),
                  },
                });
              },
            }).catch((e) => {
              handleRemoteImportError(e, EXO_SKELETON_SCOPE_NAME);
              throw e;
            }),
      ),
    [moduleName, scope, moniker, override, isLoading, version, timeoutMs],
  );
}
